import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { agentApi } from '@/shared/api/agentApi';
import { useSipStore } from '@/personas/agent/stores/sipStore';

/**
 * כפתור "נתק" בסרגל העליון — זמין בכל מסך וממצב כלשהו של שיחה.
 *
 * 🔑 מנתק **בשני המסלולים**, ובכוונה:
 *   1. הסופטפון (JsSIP) — כשהדפדפן באמת מחזיק את השיחה.
 *   2. השרת (ARI) — כשלא. שיחה שהתותח יזם, טאב שנסגר ונפתח מחדש, או
 *      ערוץ שנתקע משאירים את הנציג "בשיחה" בלי שום דרך לצאת; ניתוק
 *      בסופטפון לבדו לא היה עוזר שם.
 *
 * הכפתור מוצג כשהסופטפון בשיחה **או** כשהשרת מדווח על שיחה פתוחה —
 * בדיוק המקרה שבו הנציג תקוע ואינו רואה כלום.
 */

/** מצבים שבהם יש מה לנתק. */
const BUSY_STATES = new Set(['ringing-in', 'ringing-out', 'on-call', 'on-hold']);

export function HangupButton() {
  const sipState = useSipStore((s) => s.state);
  const sipHangup = useSipStore((s) => s.hangup);
  const [serverCall, setServerCall] = useState(false);
  const [busy, setBusy] = useState(false);

  const sipBusy = BUSY_STATES.has(sipState);

  // כשהסופטפון שקט — שואלים את השרת. זה המקרה שהכפתור נועד לו, ולכן
  // אסור להסתמך רק על מצב הדפדפן.
  const poll = useCallback(() => {
    if (sipBusy) return;
    agentApi.dialerState()
      .then((s) => setServerCall(!!s?.active_call))
      .catch(() => {});
  }, [sipBusy]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);

  if (!sipBusy && !serverCall) return null;

  async function hangup() {
    setBusy(true);
    try {
      // קודם הסופטפון (מיידי למשתמש), אחר כך השרת — כדי שגם ערוץ שהדפדפן
      // לא מכיר ייסגר.
      if (sipBusy) sipHangup();
      await agentApi.dialerHangup();
      setServerCall(false);
      toast('השיחה נותקה', 'ok');
    } catch {
      toast('הניתוק נכשל — נסו שוב', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void hangup()}
      disabled={busy}
      aria-label="נתק שיחה"
      title="ניתוק השיחה הפעילה"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        height: 32, padding: '0 12px', borderRadius: 'var(--r-pill)',
        border: 'none', cursor: busy ? 'default' : 'pointer',
        background: 'var(--danger)', color: '#fff',
        fontSize: 'var(--t-sm)', fontWeight: 600, fontFamily: 'inherit',
      }}
    >
      <Icon name="hang" size={15} />
      נתק
    </button>
  );
}
